import type { RequestSnapshot } from './diffRequestSnapshots';
import { SNAPSHOT_FIELD_LABELS, type SnapshotFieldKey } from './diffRequestSnapshots';

export type EmploymentSubmittedDisplayRow = {
  label: string;
  value: string;
  /** Included in per-attempt mutation diff (MUTATION_CHECK). */
  mutationTracked?: boolean;
};

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function cell(value: string | null | undefined): string {
  const trimmed = value?.trim();
  return trimmed ? trimmed : '—';
}

function readString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function readOccupationClassification(evidence: unknown): Record<string, unknown> | null {
  return asRecord(asRecord(evidence)?.occupationClassification);
}

function formatPhoneVerified(value: boolean | null | undefined): string {
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  return '—';
}

function pushRow(
  rows: EmploymentSubmittedDisplayRow[],
  label: string,
  value: string | null | undefined,
  options?: { mutationTracked?: boolean },
): void {
  const formatted = cell(value);
  if (formatted === '—') return;
  rows.push({
    label,
    value: formatted,
    ...(options?.mutationTracked ? { mutationTracked: true } : {}),
  });
}

const SNAPSHOT_DISPLAY: Array<{ key: SnapshotFieldKey; label: string }> = [
  { key: 'jobTitle', label: SNAPSHOT_FIELD_LABELS.jobTitle },
  { key: 'declaredStartDate', label: SNAPSHOT_FIELD_LABELS.declaredStartDate },
  { key: 'employerAddressNormalized', label: `${SNAPSHOT_FIELD_LABELS.employerAddressNormalized} (normalized)` },
  { key: 'employerPhoneNormalized', label: `${SNAPSHOT_FIELD_LABELS.employerPhoneNormalized} (normalized)` },
  { key: 'employerPhoneVerified', label: SNAPSHOT_FIELD_LABELS.employerPhoneVerified },
];

/**
 * Operator-facing submission table: employment context from the verification row/evidence
 * plus mutation-tracked fields persisted in evidence.requestSnapshot.
 */
export function buildEmploymentSubmittedDisplayRows(input: {
  snapshot: RequestSnapshot | null;
  listRow?: Record<string, unknown> | null;
  evidence?: unknown;
}): EmploymentSubmittedDisplayRow[] {
  const rows: EmploymentSubmittedDisplayRow[] = [];
  const row = input.listRow;
  const employer = asRecord(row?.employer);
  const evidence = input.evidence ?? asRecord(row)?.evidence;
  const occupation = readOccupationClassification(evidence);

  pushRow(rows, 'Declared employer', readString(employer?.legalName));

  const sectorDeclared =
    readString(occupation?.applicationSectorLabel) ?? readString(employer?.sector);
  pushRow(rows, 'Sector (declared)', sectorDeclared);

  pushRow(rows, 'Sector (normalized)', readString(occupation?.normalizedSector));

  const nocCode = readString(occupation?.resolvedNocCode);
  const nocTitle = readString(occupation?.resolvedNocTitle);
  if (nocCode || nocTitle) {
    pushRow(
      rows,
      'Occupation (NOC)',
      [nocCode, nocTitle].filter(Boolean).join(' — '),
    );
  }

  pushRow(rows, 'Linkage mode', readString(row?.linkageMode as string | undefined));

  const remoteFlag = row?.isRemote;
  if (typeof remoteFlag === 'boolean') {
    pushRow(rows, 'Remote work', remoteFlag ? 'Yes' : 'No');
  }

  if (input.snapshot) {
    for (const { key, label } of SNAPSHOT_DISPLAY) {
      if (key === 'employerPhoneVerified') {
        rows.push({
          label,
          value: formatPhoneVerified(input.snapshot.employerPhoneVerified),
          mutationTracked: true,
        });
        continue;
      }
      rows.push({
        label,
        value: cell(readString(input.snapshot[key] ?? null)),
        mutationTracked: true,
      });
    }
  }

  return rows;
}

export function hasEmploymentSubmissionDisplay(input: {
  snapshot: RequestSnapshot | null;
  listRow?: Record<string, unknown> | null;
  evidence?: unknown;
}): boolean {
  return buildEmploymentSubmittedDisplayRows(input).length > 0;
}

/** List API omits evidence; detail adds requestSnapshot and occupationClassification. */
export function employmentEvidenceNeedsDetailFetch(evidence: unknown): boolean {
  const row = asRecord(evidence);
  if (!row) return true;
  return row.requestSnapshot == null || row.occupationClassification == null;
}
